export interface RentScheduleEntry {
  dueDate: Date;
  status: "paid" | "overdue" | "upcoming";
}

/**
 * Computes the next 12 monthly rent due dates starting from the current month
 * (or next month if the current month's due date has already passed), and
 * assigns a status to each entry based on existing payments.
 *
 * Requirements 6.2, 6.4, 6.5, 6.6
 */
export function computeUpcomingRentSchedule(
  rentDayOfMonth: number,
  payments: Array<{ payment_date: string }>
): RentScheduleEntry[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Determine the starting month: current month if due date hasn't passed yet,
  // otherwise next month.
  const currentMonthDue = new Date(
    today.getFullYear(),
    today.getMonth(),
    rentDayOfMonth
  );

  const startYear = currentMonthDue < today ? today.getFullYear() : today.getFullYear();
  const startMonth = currentMonthDue < today ? today.getMonth() + 1 : today.getMonth();

  const entries: RentScheduleEntry[] = [];

  for (let i = 0; i < 12; i++) {
    const dueDate = new Date(startYear, startMonth + i, rentDayOfMonth);
    dueDate.setHours(0, 0, 0, 0);

    const status = resolveStatus(dueDate, today, payments);
    entries.push({ dueDate, status });
  }

  return entries;
}

function resolveStatus(
  dueDate: Date,
  today: Date,
  payments: Array<{ payment_date: string }>
): "paid" | "overdue" | "upcoming" {
  const dueYear = dueDate.getFullYear();
  const dueMonth = dueDate.getMonth();

  // Check for a matching payment: same month/year, payment_date <= dueDate
  const hasPaidPayment = payments.some((p) => {
    const pd = new Date(p.payment_date);
    pd.setHours(0, 0, 0, 0);
    return (
      pd.getFullYear() === dueYear &&
      pd.getMonth() === dueMonth &&
      pd <= dueDate
    );
  });

  if (hasPaidPayment) return "paid";
  if (dueDate < today) return "overdue";
  return "upcoming";
}

/**
 * Computes net rent as expectedRent minus serviceFee, managementFee, and managementFeeVat.
 * Requirements 2.1, 2.2, 2.3, 2.4
 */
export function computeNetRent(
  expectedRent: number,
  serviceFee: number,
  managementFee: number = 0,
  managementFeeVat: number = 0
): number {
  return expectedRent - serviceFee - managementFee - managementFeeVat;
}

/**
 * Computes appreciation (positive) or depreciation (negative) as
 * currentValue minus purchasePrice.
 * Requirement 3.5
 */
export function computeAppreciation(currentValue: number, purchasePrice: number): number {
  return currentValue - purchasePrice;
}
